/**
 * Logique pure du détail d'une tournée — carte Scénario actif, onglets Trajet et Horaires (tickets
 * #854, #856, #857), et le texte de la raison de recommandation du scénario actif.
 *
 * Aucune dépendance framework (pas de JSX, pas de React Query) : dérivation des étapes ordonnées
 * (Horaires), des cartes de trajet ordonnées (Trajet), des tracés par segment et des agrégats.
 * Testée par `scenario-view.test.mjs`. Les formes reprennent
 * telles quelles celles du `payload` (contrat solveur, `Scenario`) rendu par
 * `GET /logistics/simulations/:id`.
 */

import {
  LOGISTICS_DAY_SECONDS,
  LOGISTICS_VEHICLE_TYPE_LABELS,
  logisticsDepartureOfPaths,
  type LogisticsVehicleType,
  sortWeekDays,
  type WeekDay,
  weekDayLabel,
  weekDays,
  weekDayShortLabel,
} from '@gozoki/api-types'
import { formatInteger, formatSecondsAsHoursMinutes } from '@gozoki/tools'
import type {
  ScenarioPayload,
  ScenarioPlace,
  ScenarioSegment,
  SimulationDetail,
  SimulatorMapPathPlace,
} from '~/lib/logistics-simulations-query'
import { frenchDayMonthOfIso } from '~/lib/iso-date'
import { frenchList, pluralize } from '~/lib/utils'
import {
  mapPointOfHub,
  mapPointOfKitchen,
  mapPointOfPathPlace,
  mapPointOfStore,
  mapPointOfStoreProject,
  REMOVED_PLACE_LABEL,
  type SimulatorMapPoint,
} from './simulator-list'

export type { ScenarioPlace }

/** Deux références de lieu identiques (même type, même id) — clé d'égalité, pas d'identité objet. */
export const isSamePlace = (a: ScenarioPlace, b: ScenarioPlace): boolean =>
  a.type === b.type && a.id === b.id

/** Secondes depuis minuit → heure du cadran seule, « 05h05 », quel que soit le jour. */
export const formatDialHoursMinutes = (secondsSinceMidnight: number): string => {
  const normalized =
    ((secondsSinceMidnight % LOGISTICS_DAY_SECONDS) + LOGISTICS_DAY_SECONDS) % LOGISTICS_DAY_SECONDS
  const hours = Math.floor(normalized / 3600)
  const minutes = Math.floor((normalized % 3600) / 60)
  return `${String(hours).padStart(2, '0')}h${String(minutes).padStart(2, '0')}`
}

/**
 * Secondes depuis minuit du jour de départ → « 05h05 », préfixée du jour au-delà de minuit
 * (« J+1 06h10 », #903) : une tournée de nuit livre le lendemain, et une tournée a plusieurs jours
 * de départ, d'où un décalage plutôt qu'un nom de jour.
 */
export const formatHoursMinutes = (secondsSinceMidnight: number): string => {
  const days = Math.floor(secondsSinceMidnight / LOGISTICS_DAY_SECONDS)
  const dial = formatDialHoursMinutes(secondsSinceMidnight)
  return days >= 1 ? `J+${days} ${dial}` : dial
}

/** Fin estimée d'une tournée (LU-232), « — » sans horaires connus. */
export const estimatedEndLabelOf = (seconds: number | null): string =>
  seconds === null ? MISSING_VALUE : formatHoursMinutes(seconds)

/** Tous les segments, service après service, dans leur ordre — même flatten que `materializeTour`. */
const flattenedSegments = (payload: ScenarioPayload): ScenarioSegment[] =>
  payload.services.flatMap((service) => service.segments)

export interface ScenarioStep {
  place: ScenarioPlace
  /** Départ, arrivée, ou début de livraison d'un arrêt (après l'attente de son créneau). */
  timeSeconds: number
  /**
   * Arrivée du camion à un arrêt où il attend avant de livrer (créneau pas encore ouvert, minuit
   * d'une tournée de nuit, #903) ; absent sans attente.
   */
  waitingSinceSeconds?: number
  /** `true` pour une ligne de départ (atelier, ou hub d'un service) ; `false` pour une arrivée. */
  isDeparture: boolean
}

/** Arrivée sur place : celle du camion qui attend, sinon l'heure même de l'étape. */
const arrivalSecondsOf = (step: ScenarioStep): number =>
  step.waitingSinceSeconds ?? step.timeSeconds

/**
 * Étapes d'UN SEUL segment : la livraison à chaque arrêt puis, si elle n'est pas déjà couverte par
 * le dernier arrêt (même lieu, même heure d'arrivée), l'arrivée à la destination du segment.
 * Partagé par `scenarioStepsOf` (chronologie unique tous services confondus) et
 * `scenarioServiceGroupsOf` (une chronologie par service).
 *
 * Un arrêt se lit à son début de livraison (`serviceStartSeconds`), avec son arrivée quand le
 * camion attend : arrivé à 20h16, une boutique ouverte à 05h00 est livrée à « J+1 05h00 » (#903).
 * Les scénarios calculés avant ce champ n'ont que l'arrivée.
 */
const stepsOfSegment = (segment: ScenarioSegment): ScenarioStep[] => {
  const steps: ScenarioStep[] = segment.stops.map((stop) => {
    const deliverySeconds = stop.serviceStartSeconds ?? stop.arrivalSeconds
    return {
      place: stop.place,
      timeSeconds: deliverySeconds,
      ...(deliverySeconds > stop.arrivalSeconds && { waitingSinceSeconds: stop.arrivalSeconds }),
      isDeparture: false,
    }
  })

  const lastStop = segment.stops.at(-1)
  const destinationCoveredByLastStop =
    lastStop !== undefined &&
    isSamePlace(lastStop.place, segment.destination) &&
    lastStop.arrivalSeconds === segment.arrivalSeconds
  if (!destinationCoveredByLastStop) {
    steps.push({
      place: segment.destination,
      timeSeconds: segment.arrivalSeconds,
      isDeparture: false,
    })
  }

  return steps
}

/**
 * Étapes ordonnées d'un scénario (ordre de passage des boutiques, `storeVisitOrderOf`) : le départ de l'atelier (l'instant le plus tôt où un
 * segment quitte l'atelier, ou le hub d'un dernier km qui en part, LU-196), puis la livraison à chaque arrêt, triées CHRONOLOGIQUEMENT — plusieurs
 * services (véhicules) parallèles ne se lisent pas groupés par service, mais dans l'ordre où ils
 * se produisent réellement. Deux livraisons à la même seconde (deux camions qui attendent la même
 * ouverture, ou minuit) passent dans l'ordre d'arrivée. `Array.prototype.sort` est stable
 * (ES2019) : à arrivée égale aussi, elles gardent l'ordre où elles ont été poussées ci-dessous
 * (services dans l'ordre du `payload`, segments dans leur ordre, arrêts dans leur ordre).
 */
export const scenarioStepsOf = (payload: ScenarioPayload): ScenarioStep[] => {
  const segments = flattenedSegments(payload)
  const steps: ScenarioStep[] = []

  const fromKitchen = segments.filter((segment) => segment.origin.type === 'central_kitchen')
  // Un dernier km parti d'un hub (LU-196) ne passe pas par l'atelier : il part du hub.
  const departing = fromKitchen.length > 0 ? fromKitchen : segments
  if (departing.length > 0) {
    const earliest = departing.reduce((min, segment) =>
      segment.departureSeconds < min.departureSeconds ? segment : min,
    )
    steps.push({
      place: earliest.origin,
      timeSeconds: earliest.departureSeconds,
      isDeparture: true,
    })
  }

  for (const segment of segments) {
    steps.push(...stepsOfSegment(segment))
  }

  return [...steps].sort(
    (a, b) => a.timeSeconds - b.timeSeconds || arrivalSecondsOf(a) - arrivalSecondsOf(b),
  )
}

export interface ScenarioServiceGroup {
  vehicleId: number
  /** Chauffeur du service (`payload.services[].driverId`), absent des anciens payloads. */
  driverId?: number
  steps: ScenarioStep[]
}

/**
 * Étapes groupées PAR SERVICE/CAMION : un groupe par véhicule, jamais fusionné avec un autre
 * — contrairement à `scenarioStepsOf` (une chronologie unique, tous services mélangés), chaque
 * groupe garde son propre départ (origine du premier segment du service — atelier OU hub selon le
 * cas — à son heure de départ), ses arrêts, puis son retour (arrivée du dernier segment). Un
 * service à un seul segment donne un groupe départ + arrêts + retour ; un service à plusieurs
 * segments (ex. rupture de charge côté hub) enchaîne les arrêts de chaque segment sans redéclarer
 * de départ intermédiaire. Un seul service dans le `payload` → un seul groupe. `driverId` accompagne
 * le groupe pour que l'en-tête affiche le chauffeur en plus de la plaque.
 */
export const scenarioServiceGroupsOf = (payload: ScenarioPayload): ScenarioServiceGroup[] =>
  payload.services.map((service) => {
    const steps: ScenarioStep[] = []
    service.segments.forEach((segment, index) => {
      if (index === 0) {
        steps.push({
          place: segment.origin,
          timeSeconds: segment.departureSeconds,
          isDeparture: true,
        })
      }
      steps.push(...stepsOfSegment(segment))
    })
    return { vehicleId: service.vehicleId, driverId: service.driverId, steps }
  })

/** Valeur absente d'une ligne de récap : aucun jour, ou un scénario sans service. */
const MISSING_VALUE = '—'

/**
 * Jours de départ d'une tournée, en une ligne : trois jours qui se suivent ou plus en plage
 * (« Lun → Sam »), les autres un par un (« Lun, Mer, Ven », « Lun → Mer, Ven »). La semaine
 * commence le lundi : samedi, dimanche et lundi ne font pas une plage.
 */
export const activityDaysLabelOf = (days: readonly WeekDay[]): string => {
  const runs: WeekDay[][] = []
  for (const day of sortWeekDays([...new Set(days)])) {
    const run = runs.at(-1)
    const previous = run?.at(-1)
    if (
      run !== undefined &&
      previous !== undefined &&
      weekDays.indexOf(previous) + 1 === weekDays.indexOf(day)
    ) {
      run.push(day)
    } else {
      runs.push([day])
    }
  }
  if (runs.length === 0) return MISSING_VALUE
  return runs
    .flatMap((run) =>
      run.length >= 3
        ? [`${weekDayShortLabel(run[0]!)} → ${weekDayShortLabel(run.at(-1)!)}`]
        : run.map(weekDayShortLabel),
    )
    .join(', ')
}

/** Lignes de la carte « Horaires » de l'onglet du même nom (LU-223), déjà formatées. */
export interface ScenarioHoursRecap {
  days: string
  departure: string
  end: string
  weekly: string
}

/**
 * Récap de l'onglet Horaires (LU-223) : jours de départ de la simulation, départ et fin estimée
 * du scénario actif (son premier départ et son dernier passage, comme la liste en dessous), et
 * heures par semaine de la tournée, sa durée (amplitude du premier départ au dernier retour, celle
 * de la carte « Scénario actif ») par jour de départ. La durée est arrondie à la minute avant
 * d'être multipliée, comme elle s'affiche : « 3 h 14 » sur six jours fait « 19 h 24 », pas les
 * « 19 h 26 » de ses secondes. Départ et fin viennent du scénario et non des horaires saisis :
 * modifiés depuis le calcul, ceux-ci ne disent plus ce qu'il a retenu. Sans scénario actif (`null`),
 * seuls les jours s'affichent, le reste vaut « — ».
 */
export const scenarioHoursRecapOf = (
  activityDays: readonly WeekDay[],
  scenario: { payload: ScenarioPayload; totalDurationSeconds: number } | null,
): ScenarioHoursRecap => {
  const steps = scenario === null ? [] : scenarioStepsOf(scenario.payload)
  const first = steps[0]
  const last = steps.at(-1)
  const dayCount = new Set(activityDays).size
  return {
    days: activityDaysLabelOf(activityDays),
    departure: first === undefined ? MISSING_VALUE : formatHoursMinutes(first.timeSeconds),
    end: estimatedEndLabelOf(last?.timeSeconds ?? null),
    weekly:
      scenario === null || first === undefined || dayCount === 0
        ? MISSING_VALUE
        : formatSecondsAsHoursMinutes(
            Math.round(scenario.totalDurationSeconds / 60) * 60 * dayCount,
          ),
  }
}

/** Camions et chauffeurs rendus avec un scénario, résolus par l'API (détail de tournée ou de calcul). */
export interface ScenarioResources {
  vehicles: readonly { id: number; vehicleType: LogisticsVehicleType; plate: string }[]
  drivers: readonly ScenarioDriver[]
}

/**
 * Chauffeur d'un scénario : son nom, et le collaborateur dont la fiche s'ouvre (LU-175), `null`
 * quand l'API ne le rend pas (droits, périmètre RH, sous-traitant).
 */
export interface ScenarioDriver {
  id: number
  name: string
  collaboratorId: number | null
}

/** Camion qu'on ne retrouve plus (supprimé depuis le calcul, ou hors de la liste lue) : « Camion n° 4 ». */
export const vehicleFallbackLabel = (vehicleId: number | string): string => `Camion n° ${vehicleId}`

/**
 * Camions distincts d'un scénario, dans l'ordre des services, chacun avec ses chauffeurs distincts :
 * un camion qui fait plusieurs services ne compte qu'une fois. Base commune de la carte « Scénario
 * actif » et de la modale des alternatives (l'onglet Horaires garde un groupe par service).
 */
export const scenarioVehicleDriverIdsOf = (
  payload: ScenarioPayload,
): { vehicleId: number; driverIds: number[] }[] => {
  const driverIdsByVehicle = new Map<number, Set<number>>()
  for (const service of payload.services) {
    const driverIds = driverIdsByVehicle.get(service.vehicleId) ?? new Set<number>()
    if (service.driverId !== undefined) driverIds.add(service.driverId)
    driverIdsByVehicle.set(service.vehicleId, driverIds)
  }
  return [...driverIdsByVehicle].map(([vehicleId, driverIds]) => ({
    vehicleId,
    driverIds: [...driverIds],
  }))
}

/**
 * Lecture des camions et chauffeurs d'un scénario dans ses ressources. Partout la même règle : un
 * camion supprimé depuis le calcul se lit « Camion n° 4 », un chauffeur inconnu ou absent (ancien
 * payload) vaut `null`.
 */
export const scenarioResourceResolverOf = <TVehicle extends ScenarioResources['vehicles'][number]>({
  vehicles,
  drivers,
}: {
  vehicles: readonly TVehicle[]
  drivers: ScenarioResources['drivers']
}) => {
  const vehicleById = new Map(vehicles.map((vehicle) => [vehicle.id, vehicle]))
  const driverById = new Map(drivers.map((driver) => [driver.id, driver]))
  const driverOf = (driverId: number | undefined): ScenarioDriver | null =>
    driverId === undefined ? null : (driverById.get(driverId) ?? null)
  return {
    /** Camion tel que rendu par le détail, `undefined` s'il a été supprimé depuis le calcul. */
    vehicleOf: (vehicleId: number): TVehicle | undefined => vehicleById.get(vehicleId),
    /** Id de la fiche du camion, `null` s'il n'est plus dans les ressources (supprimé). */
    vehicleRecordIdOf: (vehicleId: number): number | null =>
      vehicleById.has(vehicleId) ? vehicleId : null,
    /** « AA-123-BB ». */
    plateOf: (vehicleId: number): string =>
      vehicleById.get(vehicleId)?.plate ?? vehicleFallbackLabel(vehicleId),
    /** « PL · AA-123-BB ». */
    vehicleLabelOf: (vehicleId: number): string => {
      const vehicle = vehicleById.get(vehicleId)
      return vehicle === undefined
        ? vehicleFallbackLabel(vehicleId)
        : `${LOGISTICS_VEHICLE_TYPE_LABELS[vehicle.vehicleType].short} · ${vehicle.plate}`
    },
    driverOf,
    driverNameOf: (driverId: number | undefined): string | null => driverOf(driverId)?.name ?? null,
  }
}

/** Camion retenu par le simulateur et son chauffeur (#936), sur la carte « Scénario actif ». */
export interface ScenarioResourceLine {
  vehicleId: number
  /** Id de la fiche du camion, `null` pour un camion supprimé depuis le calcul. */
  vehicleRecordId: number | null
  /** « PL · AA-123-BB » ; « Camion n° 4 » pour un camion supprimé depuis le calcul. */
  vehicle: string
  /** Chauffeurs connus de ce camion, vide sans aucun (ancien payload, chauffeur supprimé). */
  drivers: ScenarioDriver[]
}

/**
 * Camions et chauffeurs retenus par le simulateur (#936) : une ligne par camion, dans l'ordre des
 * services. Une tournée n'en a qu'un ; un scénario antérieur à la règle (#901) peut en avoir
 * plusieurs. Plaques et noms viennent du détail (`activeScenario.vehicles`, `drivers`).
 */
export const scenarioResourceLinesOf = (
  scenario: ScenarioResources & { payload: ScenarioPayload },
): ScenarioResourceLine[] => {
  const resolver = scenarioResourceResolverOf(scenario)
  return scenarioVehicleDriverIdsOf(scenario.payload).map(({ vehicleId, driverIds }) => ({
    vehicleId,
    vehicleRecordId: resolver.vehicleRecordIdOf(vehicleId),
    vehicle: resolver.vehicleLabelOf(vehicleId),
    drivers: driverIds.flatMap((driverId) => resolver.driverOf(driverId) ?? []),
  }))
}

/**
 * Remplissage d'une jauge de charge, en % de la capacité, borné à 100 ; vide sans capacité
 * déclarée (le dépassement se lit alors sur la valeur, en rouge).
 */
export const capacityFillPercentOf = (used: number, capacity: number): number =>
  capacity > 0 ? Math.min(100, Math.round((used / capacity) * 100)) : 0

/** Charge utile d'un camion : « 1 350 kg ». */
export const formatPayloadKg = (payloadKg: number): string => `${formatInteger(payloadKg)} kg`

/**
 * Indicateurs de capacité d'un camion du scénario actif (carte « Camions et capacité », #855) :
 * charge utile, palettes et palis déclarés ; « — » pour une valeur non déclarée.
 */
export const vehicleCapacityKpisOf = (vehicle: {
  payloadKg: number | null
  pallets800x1200: number | null
  palis800x600: number | null
}): { payload: string; pallets: string; palis: string } => ({
  payload: vehicle.payloadKg === null ? '—' : formatPayloadKg(vehicle.payloadKg),
  pallets: vehicle.pallets800x1200 === null ? '—' : `× ${vehicle.pallets800x1200}`,
  palis: vehicle.palis800x600 === null ? '—' : `× ${vehicle.palis800x600}`,
})

/** Distance en kilomètres arrondis (« 42 km »). */
export const formatKilometers = (meters: number): string => `${Math.round(meters / 1000)} km`

/** Clé stable d'un lieu, tous types confondus (ex. `hub-3`) : sert d'id de marqueur/point de tracé. */
export const placeKey = (place: ScenarioPlace): string => `${place.type}-${place.id}`

/** Lieu livré : une boutique, ou une boutique en projet du foncier (#941) qui compte comme elle. */
export const isDeliveredPlace = (
  place: ScenarioPlace,
): place is ScenarioPlace & { type: 'store' | 'store_project' } =>
  place.type === 'store' || place.type === 'store_project'

/** Charge au départ d'un service (`null` : payload trop ancien pour la connaître). */
export type ScenarioDepartureLoad = NonNullable<
  ScenarioPayload['services'][number]['departureLoad']
>

/**
 * Charge au départ par camion du scénario actif, calculée par l'API (repli compris pour les
 * scénarios persistés avant ce champ) : le premier service du camion.
 */
export const departureLoadByVehicleId = (
  payload: ScenarioPayload,
): Map<number, ScenarioDepartureLoad> => {
  const loads = new Map<number, ScenarioDepartureLoad>()
  for (const service of payload.services) {
    if (service.departureLoad !== undefined && !loads.has(service.vehicleId)) {
      loads.set(service.vehicleId, service.departureLoad)
    }
  }
  return loads
}

/**
 * Rang (1-based) de chaque clé à sa première apparition, dans l'ordre donné : numérotation des
 * boutiques des cartes de détail (simulation, tournée en vigueur). Une clé revue garde son rang.
 */
export const firstPassageRanksOf = (keys: Iterable<string>): Map<string, number> => {
  const ranks = new Map<string, number>()
  for (const key of keys) {
    if (!ranks.has(key)) ranks.set(key, ranks.size + 1)
  }
  return ranks
}

/**
 * Numéro d'ordre (1-based) de chaque boutique visitée, dans l'ordre chronologique de livraison tous
 * services confondus (numérotation des marqueurs boutique de la carte du détail — l'ordre est
 * « déjà disponible » via les étapes de `scenarioStepsOf`, pas de nouvelle donnée). Une boutique
 * livrée deux fois (rare) ne garde que son premier passage.
 */
export const storeVisitOrderOf = (payload: ScenarioPayload): Map<string, number> =>
  firstPassageRanksOf(
    scenarioStepsOf(payload)
      .filter((step) => isDeliveredPlace(step.place))
      .map((step) => placeKey(step.place)),
  )

export interface ActiveScenarioReasonParams {
  /** `activeScenario.isRecommended` : le scénario actif est-il celui recommandé par le moteur (dans
   * SON run) ? */
  isActiveRecommended: boolean
  /** `activeScenario.runId` : run dont est issu le scénario actif. */
  activeRunId: number
  /** `latestRun.id`, `null` sans run. */
  latestRunId: number | null
  /** `latestRun.recommendationReason`, déjà lisible (familles et hubs nommés par l'API). */
  recommendationReason: string | null
}

/**
 * Texte affiché dans l'info-bulle du scénario actif : quand l'actif N'EST PAS le recommandé
 * (sélection manuelle), le dit explicitement plutôt que de décrire la recommandation comme si elle
 * expliquait le choix actif (constat : sim 3, actif via hub, info-bulle « direct retenue »).
 *
 * La raison est celle du DERNIER run : quand le scénario actif vient d'un run plus ancien (tournée
 * validée conservée après une relance, re-sélection d'un scénario antérieur), elle ne l'explique
 * pas — famille ou coût peuvent différer de la carte — et le texte l'attribue au dernier calcul.
 */
export const activeScenarioReasonText = ({
  isActiveRecommended,
  activeRunId,
  latestRunId,
  recommendationReason,
}: ActiveScenarioReasonParams): string | null => {
  if (recommendationReason === null) return null
  if (activeRunId !== latestRunId) {
    return `Scénario issu d'un calcul précédent. Recommandation du dernier calcul : ${recommendationReason}`
  }
  return isActiveRecommended
    ? recommendationReason
    : `Choisi manuellement. Recommandation du moteur : ${recommendationReason}`
}

type DetailPlaces = Pick<
  SimulationDetail,
  'kitchen' | 'departureHub' | 'hubs' | 'stores' | 'storeProjects' | 'activeScenario'
>

/**
 * Libellé de chaque lieu du détail, par `placeKey` : ceux du périmètre, complétés par ceux des
 * tracés du scénario actif et des `extraPaths` fournis (scénarios alternatifs), lieux sortis du
 * périmètre depuis le calcul compris.
 */
export const placeLabelsOf = (
  detail: DetailPlaces,
  extraPaths: readonly (readonly SimulatorMapPathPlace[])[] = [],
): Map<string, string> => {
  // Un lieu supprimé a un libellé vide (périmètre) ou `null` (tracé) : pas d'entrée, le repli
  // de `placeLabelOrRemoved` s'applique.
  const entries: [string, string | null][] = [
    [placeKey({ type: 'central_kitchen', id: detail.kitchen.id }), detail.kitchen.label],
    // Le hub de départ d'un dernier km (LU-196) n'est pas dans `hubs`, qui ne porte que ceux de rupture.
    ...[...(detail.departureHub ? [detail.departureHub] : []), ...detail.hubs].map(
      (hub): [string, string] => [placeKey({ type: 'hub', id: hub.id }), hub.name],
    ),
    ...detail.stores.map((store): [string, string] => [
      placeKey({ type: 'store', id: store.id }),
      store.label,
    ]),
    ...detail.storeProjects.map((project): [string, string] => [
      placeKey({ type: 'store_project', id: project.id }),
      project.label,
    ]),
    ...[...(detail.activeScenario?.paths ?? []), ...extraPaths]
      .flat()
      .map((place): [string, string | null] => [placeKey(place), place.label]),
  ]
  const labels = new Map<string, string>()
  for (const [key, label] of entries) {
    if (label && !labels.has(key)) labels.set(key, label)
  }
  return labels
}

/** Libellé d'un lieu lu dans `placeLabelsOf`, `REMOVED_PLACE_LABEL` pour un lieu supprimé. */
export const placeLabelOrRemoved = (
  labels: ReadonlyMap<string, string> | null,
  place: ScenarioPlace,
): string => labels?.get(placeKey(place)) ?? REMOVED_PLACE_LABEL

/**
 * Point de départ d'un scénario, lu dans ses tracés (`logisticsDepartureOfPaths`, règle partagée
 * avec le `hubCount` du backend) : l'atelier, ou le hub d'un dernier km parti d'un hub (LU-196).
 * `null` sans tracé. Réduit au type et à l'id, sans le reste du lieu.
 */
export const scenarioDepartureOfPaths = (
  paths: readonly (readonly ScenarioPlace[])[],
): ScenarioPlace | null => {
  const place = logisticsDepartureOfPaths(paths)
  return place === null ? null : { type: place.type, id: place.id }
}

/** Point de départ du périmètre actuel : son hub de départ (LU-196), sinon son atelier. */
export const perimeterDepartureOf = (
  detail: Pick<DetailPlaces, 'departureHub' | 'kitchen'>,
): ScenarioPlace =>
  detail.departureHub
    ? { type: 'hub', id: detail.departureHub.id }
    : { type: 'central_kitchen', id: detail.kitchen.id }

/**
 * Point de départ DU SCÉNARIO actif (`scenarioDepartureOfPaths`). Celui du périmètre a pu changer
 * depuis le calcul. Sans tracé (ni scénario actif), le point de départ du périmètre.
 */
export const activeScenarioDepartureOf = (detail: DetailPlaces): ScenarioPlace =>
  scenarioDepartureOfPaths(detail.activeScenario?.paths ?? []) ?? perimeterDepartureOf(detail)

export interface DetailMapMarker extends SimulatorMapPoint {
  /** Rang de passage d'une boutique du scénario actif, `null` sinon. */
  order: number | null
}

/**
 * Marqueurs de la carte du détail : les lieux DU SCÉNARIO ACTIF (ceux de ses tracés, localisés sur
 * le lieu lui-même, même sortis du périmètre), boutiques numérotées dans l'ordre de passage ; sans
 * scénario actif, tout le périmètre de la simulation. Mêmes points que la carte de la liste
 * (`mapPointOfKitchen`, `mapPointOfPathPlace`… : clés, libellé d'un lieu supprimé, lieux non
 * géolocalisés omis).
 */
export const detailMapMarkersOf = (detail: DetailPlaces): DetailMapMarker[] => {
  const scenario = detail.activeScenario
  if (scenario !== null) return scenarioMapMarkersOf(scenario)
  const points = [
    // Un dernier km parti d'un hub (LU-196) ne passe pas par l'atelier.
    detail.departureHub ? mapPointOfHub(detail.departureHub) : mapPointOfKitchen(detail.kitchen),
    ...detail.hubs.map(mapPointOfHub),
    ...detail.stores.map(mapPointOfStore),
    ...detail.storeProjects.map(mapPointOfStoreProject),
  ]
  return uniqueMarkers(points, null)
}

/** Points dédoublonnés par clé, avec le rang de passage des boutiques s'il est connu. */
const uniqueMarkers = (
  points: readonly (SimulatorMapPoint | null)[],
  order: Map<string, number> | null,
): DetailMapMarker[] => {
  const markers = new Map<string, DetailMapMarker>()
  for (const point of points) {
    if (point === null || markers.has(point.id)) continue
    // Même clé `store-<id>` que `placeKey` pour une boutique : le rang s'y lit directement.
    markers.set(point.id, { ...point, order: order?.get(point.id) ?? null })
  }
  return [...markers.values()]
}

/**
 * Marqueurs d'un scénario : les lieux de ses tracés (localisés par l'API sur le lieu lui-même),
 * boutiques numérotées dans l'ordre de passage. Partagé par la carte du détail et les mini-cartes
 * des alternatives.
 */
export const scenarioMapMarkersOf = (scenario: {
  paths: readonly (readonly SimulatorMapPathPlace[])[]
  payload: ScenarioPayload
}): DetailMapMarker[] => pathMapMarkersOf(scenario.paths, storeVisitOrderOf(scenario.payload))

/**
 * Marqueurs des lieux de tracés (localisés par l'API sur le lieu lui-même), boutiques numérotées
 * d'après `order` (clés `placeKey`). Partagé par les cartes d'un scénario et d'une tournée en
 * vigueur, dont l'ordre vient de ses segments.
 */
export const pathMapMarkersOf = (
  paths: readonly (readonly SimulatorMapPathPlace[])[],
  order: Map<string, number>,
): DetailMapMarker[] => uniqueMarkers(paths.flat().map(mapPointOfPathPlace), order)

/**
 * Sous le camion de la tournée : la charge des camions du scénario actif, ou un texte quand il n'y
 * en a aucune à afficher. Le texte parle du scénario, pas du camion choisi au-dessus : « aucun
 * véhicule affecté » s'y lisait comme une contradiction. Un scénario calculé peut n'avoir aucun
 * camion connu : le détail ne garde que les camions de ses services encore en base (camion supprimé
 * depuis, ou scénario sans service).
 */
export const scenarioLoadOf = <V>(
  activeScenario: { vehicles: readonly V[] } | null,
): { note: string } | { vehicles: readonly V[] } => {
  if (activeScenario === null) return { note: 'Charge affichée une fois la tournée calculée.' }
  return activeScenario.vehicles.length === 0
    ? { note: 'Aucun camion connu dans le scénario actif.' }
    : { vehicles: activeScenario.vehicles }
}

/**
 * Jour dont la charge a été calculée, quand la tournée part plusieurs jours (LU-129) : le calcul
 * retient le plus chargé, sous réserve des autres jours de départ où des boutiques n'ont ni
 * projection ni saisie (décision du 23/10/2026). `null` pour un seul jour de départ, des jours de
 * charge égale ou un calcul antérieur, fait sur le premier : les diagnostics portent alors seuls
 * ces boutiques.
 */
export const busiestDepartureDayNoteOf = (
  activeScenario: {
    busiestDepartureDay: {
      date: string
      weekDay: WeekDay
      unknownLoadDays: { weekDay: WeekDay; storeCount: number }[]
    } | null
  } | null,
): string | null => {
  const day = activeScenario?.busiestDepartureDay ?? null
  if (day === null) return null
  const weekDay = weekDayLabel(day.weekDay)
  const note = `Charge calculée sur le ${weekDay} ${frenchDayMonthOfIso(day.date)}, jour de départ le plus chargé`
  if (day.unknownLoadDays.length === 0) return `${note}.`
  const unknown = frenchList(
    day.unknownLoadDays.map(
      (unknownDay) =>
        `le ${weekDayLabel(unknownDay.weekDay)} pour ${pluralize(unknownDay.storeCount, 'boutique')}`,
    ),
  )
  return `${note}, sous réserve : charge inconnue ${unknown}.`
}
